import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useRealtime } from "@/context/RealtimeContext";

export default function Listing() {
  const { categoryId } = useParams();
  const [sp] = useSearchParams();
  const q = sp.get("q") || "";
  const brand = sp.get("brand") || "";
  const sellerId = sp.get("seller_id") || "";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState("");
  const [inStock, setInStock] = useState(false);
  const [loading, setLoading] = useState(true);
  const { subscribe } = useRealtime();

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (categoryId) params.set("category_id", categoryId);
    if (q) params.set("q", q);
    if (brand) params.set("brand", brand);
    if (sellerId) params.set("seller_id", sellerId);
    if (sort) params.set("sort", sort);
    // Out-of-stock: search me dikhega (notify-me ke liye), bina search ke (browse) nahi
    const searching = !!q;
    if (searching ? inStock : true) params.set("in_stock", "true");
    params.set("limit", "100000");
    const { data } = await api.get(`/products?${params.toString()}`);
    setProducts(data); setLoading(false);
  }, [categoryId, q, brand, sellerId, sort, inStock]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("product") || m.event?.startsWith("stock")) load(); }), [subscribe, load]);

  const cat = cats.find((c) => c.id === categoryId);
  const title = cat ? cat.name : brand ? `Brand: ${brand}` : q ? `Results for "${q}"` : "All Products";
  const metaDesc = `${title} — Rani Super Market par online kharidein. Best price, fresh stock aur fast home delivery, Quality Guarantee ke saath.`;
  useEffect(() => {
    document.title = `${title} — Rani Super Market`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", metaDesc.slice(0, 160));
  }, [title, metaDesc]);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5">
      {/* Rani Super Market brand strip */}
      <div data-testid="listing-brand-strip" className="mb-5 rounded-2xl overflow-hidden shadow-md flex items-center gap-4 px-5 py-4" style={{ background: "linear-gradient(120deg,#e10600,#b30000)" }}>
        <img src="/rani-brand.png" alt="Rani Super Market" className="h-14 sm:h-16 rounded-xl object-contain bg-white/95 p-1" />
        <div className="text-white leading-tight">
          <p className="font-extrabold text-lg sm:text-xl tracking-wide">RANI <span className="text-amber-300">SUPER MARKET</span></p>
          <p className="text-[11px] sm:text-xs font-bold text-amber-200 tracking-widest">QUALITY, GUARANTEE KE SAATH</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="listing-title">{title} <span className="text-slate-400 text-base font-medium">({products.length})</span></h1>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm font-medium text-slate-600 cursor-pointer">
            <input data-testid="filter-in-stock" type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} className="accent-emerald-600 w-4 h-4" /> In stock
          </label>
          <div className="relative flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-2">
            <SlidersHorizontal size={15} className="text-slate-400" />
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm font-medium outline-none bg-transparent">
              <option value="">Relevance</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="name">Name</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => <div key={i} className="bg-white rounded-2xl h-64 animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="no-results">
          <img src="/rani-brand.png" alt="Rani Super Market" className="h-20 mx-auto rounded-2xl shadow-md mb-5" />
          <p className="font-semibold text-slate-500">"{q}" ke liye kuch nahi mila.</p>
          <p className="text-sm mt-1">Kuch aur search karein — Rani Super Market, Quality Guarantee ke Saath!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}

      <section className="mt-10 border-t border-slate-200 pt-6 text-sm text-slate-500 leading-relaxed space-y-3" data-testid="listing-seo-text">
        <h2 className="text-base font-bold text-slate-700">{title} online — Rani Super Market</h2>
        <p>Rani Super Market par {title.toLowerCase()} online order karein — har product quality aur guarantee ke saath. Hum daily essentials, groceries, fresh fruits &amp; vegetables, dairy, packaged food, household items aur electronics best prices par offer karte hain, seedhe aapke ghar tak fast delivery ke saath.</p>
        <p>Rs. Mart POS se powered Rani Super Market ek trusted local aur online store hai. In-stock filter aur price/name sorting se apni pasand ka product asaani se dhoondein, secure checkout par UPI ya Cash on Delivery se payment karein, aur har order par reward points kamayein. Naye offers aur festival coupons ke liye regular visit karte rahein — Quality, Guarantee Ke Saath.</p>
      </section>
    </div>
  );
}
